import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { Button, Dialog, DialogActions, DialogTitle, IconButton, Paper, type PaperProps } from "@mui/material";
import useForkRef from "@mui/utils/useForkRef";
import clsx from "clsx";
import { forwardRef, useRef, type MouseEvent } from "react";
import Draggable from "react-draggable";
import { useMobile } from "@/hooks/useMobile";
import { buildMobileDialogPaperProps } from "../dialog/mobileDialogLayout";
import type { AppDialogDefaultProps } from "./AppDialogDefault.types";

// ------------------------------- CONSTANTS -------------------------------
const DIALOG_DRAG_HANDLE = '[data-app-dialog-drag-handle="true"]';
const DIALOG_DRAG_CANCEL = 'button, a, input, textarea, select, [role="button"], [contenteditable="true"]';

// ------------------------------- COMPONENTS ------------------------------
const DraggableDialogPaper = forwardRef<HTMLDivElement, PaperProps>(function DraggableDialogPaper(props, forwardedRef) {
	const nodeRef = useRef<HTMLDivElement>(null);
	const paperRef = useForkRef(nodeRef, forwardedRef);

	return (
		<Draggable
			nodeRef={nodeRef}
			handle={DIALOG_DRAG_HANDLE}
			cancel={DIALOG_DRAG_CANCEL}
			bounds="parent"
			enableUserSelectHack={false}
		>
			<Paper {...props} ref={paperRef} />
		</Draggable>
	);
});

// ------------------------------- COMPONENT -------------------------------
export default function AppDialogDefault({
	aberto,
	onFechar,
	aoTeclaPressionada,
	titulo,
	subtitulo,
	icone,
	children,
	maxWidth = "md",
	fullWidth = true,
	arrastavel = false,
	acoesCabecalho,
	acoesRodape,
	exibirRodape = true,
	exibirBotaoFecharCabecalho = true,
	exibirBotaoFecharRodape = true,
	textoBotaoFechar = "Fechar",
	corBotaoFechar = "error",
	varianteBotaoFechar = "contained",
	desabilitarBotaoFecharCabecalho = false,
	desabilitarBotaoFecharRodape = false,
	impedirFecharBackdrop = false,
	paperClassName,
	paperSx,
	paperMobileSx,
	contentClassName,
	titleClassName,
	bodyClassName,
	actionsClassName,
	disableEscapeKeyDown,
}: Readonly<AppDialogDefaultProps>) {
	const isMobile = useMobile();
	const permitirArraste = arrastavel && !isMobile;

	const impedirPropagacaoEventoDialog = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
	};

	const paperProps = {
		...buildMobileDialogPaperProps(isMobile, {
			className: clsx("bg-background text-foreground", paperClassName),
			sx: paperSx,
			mobileSx: paperMobileSx,
		}),
		style: permitirArraste ? { willChange: "transform" } : undefined,
		onMouseDown: impedirPropagacaoEventoDialog,
		onClick: impedirPropagacaoEventoDialog,
		onDoubleClick: impedirPropagacaoEventoDialog,
	};

	const handleDialogClose = (_: object, reason: "backdropClick" | "escapeKeyDown") => {
		if (impedirFecharBackdrop && reason === "backdropClick") return;

		onFechar();
	};

	return (
		<Dialog
			open={aberto}
			onClose={handleDialogClose}
			onKeyDown={aoTeclaPressionada}
			fullWidth={fullWidth}
			maxWidth={maxWidth}
			disableEscapeKeyDown={disableEscapeKeyDown}
			slots={permitirArraste ? { paper: DraggableDialogPaper } : undefined}
			slotProps={{
				paper: paperProps,
			}}
		>
			<div className={clsx("flex min-h-0 flex-1 flex-col overflow-hidden", contentClassName)}>
				<DialogTitle
					data-app-dialog-drag-handle={permitirArraste ? "true" : undefined}
					className={clsx(
						"flex min-w-0 shrink-0 items-center gap-2 border-b bg-primary px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3",
						permitirArraste && "cursor-move select-none",
						titleClassName,
					)}
				>
					<div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
						{icone ? (
							<FuseSvgIcon className="shrink-0 text-primary-foreground" size={isMobile ? 22 : 28}>
								{icone}
							</FuseSvgIcon>
						) : null}

						<div className="min-w-0 flex-1 overflow-hidden border-border/50">
							<h1 className="block max-w-full truncate text-base font-semibold leading-5 text-primary-foreground sm:text-lg sm:leading-6">
								{titulo}
							</h1>

							{subtitulo ? (
								<h3 className="block max-w-full truncate text-xs font-semibold leading-5 text-primary-foreground/90 sm:text-md sm:leading-6">
									{subtitulo}
								</h3>
							) : null}
						</div>
					</div>

					<div className="ml-auto flex shrink-0 items-center gap-1">
						{acoesCabecalho}

						{exibirBotaoFecharCabecalho ? (
							<IconButton
								className="h-8 w-8 shrink-0 text-primary-foreground"
								size="small"
								aria-label="Fechar"
								disabled={desabilitarBotaoFecharCabecalho}
								onClick={onFechar}
							>
								<FuseSvgIcon size={18}>lucide:x</FuseSvgIcon>
							</IconButton>
						) : null}
					</div>
				</DialogTitle>

				<div className={clsx("flex min-h-0 flex-1 flex-col overflow-hidden px-4", bodyClassName)}>
					{children}
				</div>
			</div>

			{exibirRodape ? (
				<DialogActions
					className={clsx(
						"sticky bottom-0 flex shrink-0 justify-end border-t bg-background py-2 px-4",
						actionsClassName,
					)}
				>
					{acoesRodape}

					{exibirBotaoFecharRodape ? (
						<Button
							onClick={onFechar}
							size="small"
							variant={varianteBotaoFechar}
							color={corBotaoFechar}
							disabled={desabilitarBotaoFecharRodape}
						>
							{textoBotaoFechar}
						</Button>
					) : null}
				</DialogActions>
			) : null}
		</Dialog>
	);
}
